import {
  BlockTitle,
  ListRow,
  ListRows,
  PageTitle,
  StatusTag,
  Surface,
  formatCurrency,
  formatPhone,
} from '@westhetics/ui'
import { useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { BackLink, ReadingSkeleton, noteText, supportText } from '@/app/shell'
import { dateEyebrow, formatDateTimeIn } from '@/lib/format'
import { routes } from '@/routes'
import { useAppointment } from './api'
import { practitionerLine } from './labels'

/**
 * O atendimento (docs/areas/portal-do-paciente.md, "Atendimento"; RN-POR-04, RN-POR-15): os
 * procedimentos, a profissional, os documentos com o status por extenso, o valor combinado, a ficha
 * de saúde usada, o plano de tratamento encerrado (RN-POR-23) e a clínica. Arquétipo: ficha em
 * leitura, no celular.
 */

export function AppointmentPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const query = useAppointment(id)

  if (query.isPending) return <ReadingSkeleton label="Carregando o atendimento" />
  if (query.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={routes.home}>Voltar ao início</BackLink>
        <LoadError
          title="Não conseguimos abrir este atendimento"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const a = query.data
  return (
    <div className="flex flex-col gap-6">
      <BackLink to={routes.home}>Voltar ao início</BackLink>
      <PageTitle
        eyebrow={`Atendimento · ${dateEyebrow(a.date)} · ${a.clinic.name}`}
        title={a.title}
        meta={practitionerLine(a.practitioner)}
      />

      <section aria-label="Documentos" className="flex flex-col gap-3">
        <BlockTitle title="Documentos" note={a.summary} />
        {a.documents.length === 0 ? (
          <p className={noteText}>Nenhum documento disponível neste atendimento.</p>
        ) : (
          <ListRows>
            {a.documents.map((d) => {
              const { date } = formatDateTimeIn(d.generatedAt, a.clinic.timezone)
              const canOpen = d.canRead || d.hasPdf
              return (
                <ListRow
                  key={d.id}
                  size="name"
                  title={d.name}
                  meta={[d.version ? `versão ${d.version}` : null, `gerado em ${date}`]
                    .filter(Boolean)
                    .join(' · ')}
                  aria-label={d.name}
                  trailing={<StatusTag level={d.level}>{d.label}</StatusTag>}
                  onClick={canOpen ? () => navigate(routes.document(d.id)) : undefined}
                />
              )
            })}
          </ListRows>
        )}
      </section>

      {a.totalAmountCents !== null ? (
        <section aria-label="Valor combinado" className="flex flex-col gap-3">
          <BlockTitle title="Valor combinado" note="O mesmo que está no contrato." />
          <Surface>
            <div className="font-display text-hero text-ink font-light">
              {formatCurrency(a.totalAmountCents / 100)}
            </div>
            {a.paymentTerms ? <p className={`${supportText} mt-2`}>{a.paymentTerms}</p> : null}
          </Surface>
        </section>
      ) : null}

      {a.anamnesisId ? (
        <section aria-label="Ficha de saúde usada" className="flex flex-col gap-3">
          <BlockTitle title="Ficha de saúde usada" />
          <ListRows>
            <ListRow
              title="Ficha de saúde"
              meta="As respostas que a profissional leu antes deste atendimento."
              aria-label="Abrir a ficha de saúde usada"
              onClick={() => navigate(routes.anamnesis(a.anamnesisId!))}
            />
          </ListRows>
        </section>
      ) : null}

      {a.treatmentPlanId ? (
        <section aria-label="Plano de tratamento" className="flex flex-col gap-3">
          <BlockTitle title="Plano de tratamento" />
          <ListRows>
            <ListRow
              title="Plano de tratamento"
              meta="Os produtos aplicados, com lote e validade, e as fotos de cada etapa."
              aria-label="Abrir o plano de tratamento"
              trailing={<StatusTag level="done">Encerrado</StatusTag>}
              onClick={() => navigate(routes.treatmentPlan(a.treatmentPlanId!))}
            />
          </ListRows>
        </section>
      ) : null}

      <section aria-label="Clínica" className="flex flex-col gap-3">
        <BlockTitle title="Clínica" />
        <Surface>
          <div className="font-display text-name text-ink">{a.clinic.name}</div>
          <p className={`${supportText} mt-1`}>{a.clinic.address}</p>
          {a.clinic.phone ? (
            <p className={`${supportText} mt-1`}>{formatPhone(a.clinic.phone)}</p>
          ) : null}
        </Surface>
      </section>
    </div>
  )
}
